import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, List, ListItem, ListItemButton, Typography, IconButton, Avatar, Badge, TextField, InputAdornment } from '@mui/material';
import { Add, Group, Search, Person, ArrowBack } from '@mui/icons-material';
import { getConversations, createConversation, markConversationRead } from '../../../../api/chat';
import { socket } from '../../../../api';
import ChatWindow from './ChatWindow';
import NewConversationDialog from './NewConversationDialog';

function formatConvTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const msgDay = new Date(date);
  msgDay.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today - msgDay) / 86400000);
  if (diffDays === 0) return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
  return date.toLocaleDateString([], { month: 'numeric', day: 'numeric', year: '2-digit' });
}

export default function Chat({ access, user, onConversationRead, socketStatus, selectedConversation, onConversationChange, onClose }) {
  const onCloseRef = useRef(onClose);
  
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState(new Set());



  const [openNewChat, setOpenNewChat] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);


  const loadConversations = useCallback(async (pageNum = 0, append = false) => {
    if (loading) return;
    setLoading(true);
    try {
      const data = await getConversations(20, pageNum * 20);
      const newConversations = data || [];
      
      if (newConversations.length < 20) {
        setHasMore(false);
      }
      
      if (append) {
        setConversations(prev => [...prev, ...newConversations]);
      } else {
        setConversations(newConversations);
        setFilteredConversations(newConversations);
        setPage(0);
        setHasMore(newConversations.length >= 20);
      }
    } catch (err) {
      console.error('Failed to load conversations:', err);
      if (!append) {
        setConversations([]);
        setFilteredConversations([]);
      }
    }
    setLoading(false);
  }, [loading]);

  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevSocketStatus = useRef(socketStatus);
  
  // Refresh conversations when socket reconnects
  useEffect(() => {
    if (socketStatus && !prevSocketStatus.current) {
      loadConversations().then(() => {
        // If a conversation is currently open, mark it as read
        if (selectedConversation) {
          markConversationRead(selectedConversation).then(() => {
            setConversations(prev => prev.map(c => 
              c.id === selectedConversation ? { ...c, unreadCount: 0 } : c
            ));
          }).catch(console.error);
        }
      });
    }
    prevSocketStatus.current = socketStatus;
  }, [socketStatus, selectedConversation, loadConversations]);

  useEffect(() => {
    if (!socket) return;
    
    const handleSocketMessage = (data) => {
      if (data?.coll === 'messages' && data?.type === 'insert') {
        // Reload conversations to update unread counts and last message
        loadConversations().then(() => {
          // Notify parent about potential unread count changes
          onConversationRead?.();
        });
      }
    };
    
    const handleMessageRead = (data) => {
      if (data?.conversationId) {
        // Update the specific conversation's unread count
        setConversations(prev => prev.map(c => 
          c.id === data.conversationId ? { ...c, unreadCount: 0 } : c
        ));
      }
    };
    
    const handleNewConversation = () => {
      // Refresh conversations when a new one is created
      loadConversations().then(() => {
        // Notify parent about potential unread count changes
        onConversationRead?.();
      });
    };
    
    const handleUserOnline = (data) => {
      if (data?.userId) {
        setOnlineUsers(prev => new Set([...prev, data.userId]));
        // Update conversations to reflect online status
        setConversations(prev => prev.map(conv => ({
          ...conv,
          participants: conv.participants.map(p => 
            p.id === data.userId ? { ...p, isOnline: true } : p
          )
        })));
      }
    };
    
    const handleUserOffline = (data) => {
      if (data?.userId) {
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(data.userId);
          return newSet;
        });
        // Update conversations to reflect offline status
        setConversations(prev => prev.map(conv => ({
          ...conv,
          participants: conv.participants.map(p => 
            p.id === data.userId ? { ...p, isOnline: false } : p
          )
        })));
      }
    };
    

    
    socket.on('sub', handleSocketMessage);
    socket.on('messageRead', handleMessageRead);
    socket.on('newConversation', handleNewConversation);
    socket.on('userOnline', handleUserOnline);
    socket.on('userOffline', handleUserOffline);

    
    return () => {
      socket.off('sub', handleSocketMessage);
      socket.off('messageRead', handleMessageRead);
      socket.off('newConversation', handleNewConversation);
      socket.off('userOnline', handleUserOnline);
      socket.off('userOffline', handleUserOffline);

    };
  }, [loadConversations, user?.id, onConversationRead]);

  const handleNewConversation = async (participantIds, isGroup, groupName) => {
    if (!participantIds || participantIds.length === 0) {
      alert('Please select at least one participant.');
      return;
    }
    try {
      const result = await createConversation(participantIds, isGroup, groupName);
      if (result?.id) {
        setOpenNewChat(false);
        await loadConversations();
        onConversationChange?.(result.id);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('Failed to create conversation:', err);
      alert(err.message || 'Failed to create conversation. Please try again.');
    }
  };

  const getConversationName = useCallback((conv) => {
    if (!conv) return 'Unknown Chat';
    if (conv.isGroup) return conv.groupName || 'Group Chat';
    const currentUserId = user?.id;
    const otherParticipants = conv.participants?.filter(p => p.id !== currentUserId) || [];
    return otherParticipants.map(p => p.name || 'Unknown User').join(', ') || 'Chat';
  }, [user?.id]);

  const isConversationOnline = useCallback((conv) => {
    if (!conv || conv.isGroup) return false;
    const currentUserId = user?.id;
    const otherParticipants = conv.participants?.filter(p => p.id !== currentUserId) || [];
    // Use isOnline flag from API response first, fallback to socket state
    return otherParticipants.some(p => p.isOnline || onlineUsers.has(p.id));
  }, [user?.id, onlineUsers]);

  const handleConversationUpdate = () => {
    loadConversations();
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredConversations(conversations);
    } else {
      const filtered = conversations.filter(conv => {
        try {
          const name = getConversationName(conv).toLowerCase();
          return name.includes(searchQuery.toLowerCase());
        } catch (err) {
          console.error('Error filtering conversation:', err);
          return false;
        }
      });
      setFilteredConversations(filtered);
    }
  }, [searchQuery, conversations, getConversationName]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredConversations(conversations);
    }
  }, [conversations, searchQuery]);

  const handleLeaveConversation = () => {
    onConversationChange?.(null);
    loadConversations();
  };

  return (
    <Box sx={{ display: 'flex', height: '100dvh', bgcolor: 'background.default', overflow: 'hidden' }}>
      <Box sx={{
        width: { xs: '100%', sm: 360 },
        flexShrink: 0,
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
        display: { xs: selectedConversation ? 'none' : 'flex', sm: 'flex' },
        flexDirection: 'column',
        height: '100%'
      }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton 
                onClick={() => onCloseRef.current?.()}
                sx={{ 
                  display: 'none',
                  '@media (max-width: 768px)': {
                    display: 'flex'
                  }
                }}
              >
                <ArrowBack />
              </IconButton>
              <Typography variant="h5" fontWeight="bold">Chats</Typography>
            </Box>
            <IconButton onClick={() => setOpenNewChat(true)} color="primary">
              <Add />
            </IconButton>
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <List 
          sx={{ flex: 1, overflow: 'auto' }}
          onScroll={(e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.target;
            if (scrollHeight - scrollTop <= clientHeight * 1.5 && hasMore && !loading && !searchQuery) {
              const nextPage = page + 1;
              setPage(nextPage);
              loadConversations(nextPage, true);
            }
          }}
        >
          {filteredConversations.map((conv) => (
            <ListItem key={conv.id} disablePadding>
              <ListItemButton 
                selected={selectedConversation === conv.id}
                onClick={async () => {
                  onConversationChange?.(conv.id);
                  if (conv.unreadCount > 0) {
                    try {
                      await markConversationRead(conv.id);
                      onConversationRead?.();
                      // Update the conversation in the local state
                      setConversations(prev => prev.map(c => 
                        c.id === conv.id ? { ...c, unreadCount: 0 } : c
                      ));
                    } catch (error) {
                      console.error('Failed to mark conversation as read:', error);
                    }
                  }
                }}
              >
                <Badge badgeContent={conv.unreadCount} color="error" sx={{ mr: 2 }}>
                  <Box sx={{ position: 'relative' }}>
                    <Avatar sx={{ 
                      bgcolor: 'black', 
                      color: 'white',
                      border: '1px solid white'
                    }}>
                      {conv.isGroup ? <Group /> : 
                        conv.participants?.filter(p => p.id !== user?.id)[0]?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || <Person />
                      }
                    </Avatar>
                    {!conv.isGroup && isConversationOnline(conv) && (
                      <Box sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: 12,
                        height: 12,
                        bgcolor: 'success.main',
                        borderRadius: '50%',
                        border: '2px solid white'
                      }} />
                    )}
                  </Box>
                </Badge>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography 
                      variant="subtitle1" 
                      fontWeight={conv.unreadCount > 0 ? 'bold' : 'normal'}
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {getConversationName(conv)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1, flexShrink: 0 }}>
                      {formatConvTime(conv.lastMessage)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        flex: 1, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        fontWeight: conv.unreadCount > 0 ? 'bold' : 'normal'
                      }}
                    >
                      {conv.lastMessageContent || 'No messages yet'}
                    </Typography>
                    {conv.isGroup && (
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {conv.participants.length} members
                      </Typography>
                    )}
                  </Box>
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
          {loading && (
            <ListItem>
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', p: 2 }}>
                <Typography variant="body2" color="text.secondary">Loading...</Typography>
              </Box>
            </ListItem>
          )}
        </List>
      </Box>

      <Box sx={{ flex: 1, display: { xs: selectedConversation ? 'flex' : 'none', sm: 'flex' }, flexDirection: 'column', minWidth: 0, height: '100%', overflow: 'hidden' }}>
        {selectedConversation ? (
          <ChatWindow 
            conversationId={selectedConversation} 
            conversation={conversations.find(c => c.id === selectedConversation)}
            onUpdate={handleConversationUpdate}
            onLeave={handleLeaveConversation}
            access={access}
            user={user}
            socketStatus={socketStatus}
            onClose={() => onConversationChange?.(null)}
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 3 }}>
            <Typography variant="h6" color="text.secondary">Select a conversation to start chatting</Typography>
          </Box>
        )}
      </Box>

      <NewConversationDialog 
        open={openNewChat}
        onClose={() => setOpenNewChat(false)}
        onCreate={handleNewConversation}
        access={access}
        user={user}
      />



    </Box>
  );
}
